
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// MUI Components
import {
  Box,
  Paper,
  Typography,
  Chip,
  Avatar,
  Grid,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Pagination,
  Collapse,
  Divider,
  Stack,
  LinearProgress,
  Badge,
  Button,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import {
  describeSubscription,
  TONE_SX,
  PLATFORM_LABELS,
} from "../utils/subscription";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// Icons
import {
  FiDatabase,
  FiZap,
  FiMoon,
  FiTrendingUp,
  FiActivity,
  FiSmartphone,
  FiWifi,
  FiWifiOff,
  FiRotateCcw,
  FiClock,
  FiEye,
  FiCreditCard,
} from "react-icons/fi";

import OverviewScheduleDialog, {
  resolveRunAt,
} from "./user-detail/OverviewScheduleDialog";
import { deviceNameOnly } from "../utils/deviceName";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function UserIntelligenceLedger() {
  const { userId } = useParams();
  const token = useSelector((state) => state.user.token);

  // --- STATE ---
  const [view, setView] = useState("ledger");
  const [mode, setMode] = useState("upcoming");
  const [deviceFilter, setDeviceFilter] = useState("all");
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // The row itself is the detail source - both feeds already carry the full
  // `when` and the actions, so opening the dialog costs no request. See
  // OverviewScheduleDialog for why refetching would be wrong here.
  const [detailItem, setDetailItem] = useState(null);

  const themeColor = mode === "history" ? "#10b981" : "#6366f1";
  let isAdmin = false;
  isAdmin =
    stats?.profile?.auth?.role?.includes("admin") ||
    stats?.profile?.auth?.role?.includes("user")
      ? true
      : false;

  // The backend derives status/access from RevenueCat; this only picks the
  // wording. See src/utils/subscription.js.
  const subscription = describeSubscription(stats?.subscription);

  // Dates only — these are billing dates, the time of day is noise here.
  const fmtDate = (iso) => (iso ? dayjs(iso).format("MMM DD, YYYY") : "—");
  const fmtRange = (from, to) => {
    if (!from && !to) return "—";
    if (!from) return `until ${fmtDate(to)}`;
    if (!to) return `from ${fmtDate(from)}`;
    return `${fmtDate(from)} → ${fmtDate(to)}`;
  };

  // --- HELPERS ---
  const formatDisplay = (iso, showHebrew = false) => {
    if (!iso) return "";
    const date = new Date(iso);
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const gregorian = dayjs(date).tz
      ? dayjs(date).tz(userTz).format("MMM DD, YYYY • hh:mm A")
      : dayjs(date).format("MMM DD, YYYY • hh:mm A");
    if (!showHebrew) return gregorian;
    const hebrewDate = new Intl.DateTimeFormat("en-u-ca-hebrew", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: userTz,
    }).format(date);
    return `${gregorian} (${hebrewDate})`;
  };

  const loadStats = useCallback(async () => {
    if (!token || !userId) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/user-stats`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId }),
        },
      );
      const result = await response.json();
      if (result.code === 200) setStats(result.data);
    } catch (err) {
      console.error("Stats Error:", err);
    }
  }, [token, userId]);

  const loadData = useCallback(
    async (currentPage = 1) => {
      if (!token || !userId) return;
      setLoading(true);
      const API_URL =
        mode === "history"
          ? `${process.env.REACT_APP_API_BASE_URL}/api/historylogs/search`
          : `${process.env.REACT_APP_API_BASE_URL}/api/calender/simulate/all`;

      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "x-client-type": "web",
          },
          body: JSON.stringify({
            page: currentPage,
            limit: 10,
            userId,
            startDate: startDate.utc().startOf("day").format(),
            endDate: endDate.utc().endOf("day").format(),
          }),
        });
        const result = await response.json();
        if (result?.success || result?.code === 200) {
          setData(result.data || []);
          console.log(result.data);
          setTotalPages(result.pagination?.totalPages || 1);
        }
      } catch (err) {
        console.error("Logs Error:", err);
      } finally {
        setLoading(false);
      }
    },
    [mode, startDate, endDate, token, userId],
  );

  useEffect(() => {
    loadData(page);
    loadStats();
  }, [loadData, loadStats, page]);

  const allDevices = stats?.infrastructure?.devices?.devices || [];
  const filteredDevices = allDevices
    .filter((d) => {
      if (deviceFilter === "all") return true;
      if (deviceFilter === "online") return d.status === "online";
      if (deviceFilter === "offline") return d.status === "offline";
      if (deviceFilter === "restore") return d.smartRestore?.enabled;
      return true;
    })
    .sort(
      (a, b) =>
        b.smartRestore?.enabled - a.smartRestore?.enabled ||
        (b.status === "online" - a.status) === "online",
    );

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        bgcolor: "#f8fafc",
        minHeight: "100vh",
        maxWidth: "100vw",
      }}
    >
      {/* --- TOP PROFILE BAR --- */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "20px",
          mb: 4,
          background: "#0f172a",
          color: "white",
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid
            item
            xs={12}
            md={7}
            sx={{ display: "flex", alignItems: "center", gap: 3 }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: stats?.profile?.auth?.verified
                    ? "#10b981"
                    : "#f59e0b",
                  width: 14,
                  height: 14,
                  border: "2px solid #0f172a",
                },
              }}
            >
              <Avatar
                src={stats?.profile?.info?.avatar}
                sx={{
                  width: 64,
                  height: 64,
                  border: "2px solid rgba(255,255,255,0.1)",
                }}
              />
            </Badge>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                {stats?.profile?.info?.name}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {stats?.profile?.info?.email}
              </Typography>
              {isAdmin ? (
                <></>
              ) : (
                <Stack direction="row" spacing={1} mt={1}>
                  {/* The state, and separately whether it actually grants
                      access — they are not the same thing. A "Cancelling" or
                      "Billing issue" user still has Pro until the period ends,
                      which the old green chip could not express. */}
                  <Chip
                    label={subscription.label}
                    title={subscription.hint}
                    size="small"
                    sx={{
                      ...TONE_SX[subscription.tone],
                      fontWeight: 700,
                      fontSize: "0.65rem",
                    }}
                  />
                  <Chip
                    label={subscription.hasAccess ? "Has access" : "No access"}
                    title={
                      subscription.hasAccess
                        ? "Pro features are available to this user"
                        : "Schedule changes are blocked with HTTP 402"
                    }
                    size="small"
                    sx={{
                      ...TONE_SX[subscription.hasAccess ? "success" : "danger"],
                      fontWeight: 700,
                      fontSize: "0.65rem",
                    }}
                  />
                </Stack>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack
              direction="row"
              spacing={4}
              justifyContent={{ md: "flex-end" }}
            >
              <HeaderMetric label="DEVICES" value={allDevices.length} />
              <HeaderMetric
                label="ONLINE"
                value={stats?.infrastructure?.devices?.online}
                color="#10b981"
              />
              <HeaderMetric
                label="OFFLINE"
                value={stats?.infrastructure?.devices?.offline}
                color="#ef4444"
              />
            </Stack>
          </Grid>
          {/* --- NAV TOGGLE --- */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
            <Paper
              elevation={0}
              sx={{ p: 0.5, bgcolor: "#e2e8f0", borderRadius: "14px" }}
            >
              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={(e, v) => v && setView(v)}
                size="small"
              >
                <NavButton
                  value="ledger"
                  icon={<FiActivity />}
                  label="Schedules"
                />
                <NavButton
                  value="hardware"
                  icon={<FiSmartphone />}
                  label="Devices"
                />
              </ToggleButtonGroup>
            </Paper>
          </Box>
        </Grid>
      </Paper>

      {/* --- NAV TOGGLE ---
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <Paper elevation={0} sx={{ p: 0.5, bgcolor: "#e2e8f0", borderRadius: "14px" }}>
          <ToggleButtonGroup value={view} exclusive onChange={(e, v) => v && setView(v)} size="small">
            <NavButton value="ledger" icon={<FiActivity />} label="Log Ledger" />
            <NavButton value="hardware" icon={<FiSmartphone />} label="Hardware Registry" />
          </ToggleButtonGroup>
        </Paper>
      </Box> */}

      {/* --- UNIFIED FLEX WRAPPER --- */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        {/* --- LEFT SIDEBAR --- */}
        <Box sx={{ width: { xs: "100%", lg: "340px" }, flexShrink: 0 }}>
          <Stack spacing={3}>
            {!isAdmin && (
              <DashboardSection title="Subscription" icon={<FiCreditCard />}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#f8fafc",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <DetailRow label="State" value={subscription.label} highlight />
                  <DetailRow
                    label="Access"
                    value={subscription.hasAccess ? "Yes" : "No"}
                  />
                  <Divider sx={{ my: 1.5 }} />
                  <DetailRow
                    label="Trial"
                    value={fmtRange(subscription.trialStart, subscription.trialEnd)}
                  />
                  <DetailRow
                    label={subscription.autoRenew ? "Renews" : "Ends"}
                    value={fmtDate(subscription.currentPeriodEnd)}
                  />
                  <DetailRow
                    label="Auto-renew"
                    value={subscription.autoRenew ? "On" : "Off"}
                  />
                  <Divider sx={{ my: 1.5 }} />
                  <DetailRow label="Product" value={subscription.productId || "—"} />
                  <DetailRow
                    label="Store"
                    value={
                      PLATFORM_LABELS[subscription.platform] ||
                      subscription.platform ||
                      "—"
                    }
                  />
                  {/* Where the dates came from. "Server trial" means RevenueCat
                      has never reported on this user, so these are the legacy
                      server-clock trial, not store-verified facts. */}
                  <DetailRow
                    label="Verified by"
                    value={
                      subscription.isStoreVerified
                        ? "RevenueCat"
                        : subscription.source === "legacy_trial"
                          ? "Server trial"
                          : "—"
                    }
                  />
                </Box>
              </DashboardSection>
            )}

            <DashboardSection title="Halachic Protocol" icon={<FiMoon />}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f8fafc",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <DetailRow
                  label="Preset"
                  value={stats?.automations?.halachicOffsets?.preset}
                  highlight
                />
                <Divider sx={{ my: 1.5 }} />
                <DetailRow
                  label="Candle Lighting"
                  value={`${stats?.automations?.halachicOffsets?.offsets?.candleLightingMinutesBeforeShkiah}m`}
                />
                <DetailRow
                  label="Motzei Shabbos"
                  value={`${stats?.automations?.halachicOffsets?.offsets?.motzeiShabbosMinutesAfterShkiah}m`}
                />
              </Box>
            </DashboardSection>

            <DashboardSection title="Traffic Analysis" icon={<FiTrendingUp />}>
              <Stack spacing={2.5}>
                {stats?.analytics?.traffic?.map((t) => (
                  <Box key={t.action}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.8,
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={800}
                        color="textSecondary"
                      >
                        {t.action.toUpperCase()}
                      </Typography>
                      <Typography variant="caption" fontWeight={900}>
                        {t.totalCalls} hits
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(t.totalCalls * 5, 100)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "#f1f5f9",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: "#6366f1",
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </DashboardSection>
          </Stack>
        </Box>

        {/* --- RIGHT CONTENT AREA --- */}
        <Box sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}>
          {view === "ledger" ? (
            <>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(e, v) => v && setMode(v)}
                  size="small"
                >
                  <ToggleButton value="history" sx={{ px: 3, fontWeight: 800 }}>
                    History
                  </ToggleButton>
                  <ToggleButton
                    value="upcoming"
                    sx={{ px: 3, fontWeight: 800 }}
                  >
                    Upcoming
                  </ToggleButton>
                </ToggleButtonGroup>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack direction="row" spacing={1}>
                    <DatePicker
                      value={startDate}
                      onChange={setStartDate}
                      slotProps={{
                        textField: { size: "small", sx: { width: 130 } },
                      }}
                    />
                    <DatePicker
                      value={endDate}
                      onChange={setEndDate}
                      slotProps={{
                        textField: { size: "small", sx: { width: 130 } },
                      }}
                    />
                  </Stack>
                </LocalizationProvider>
              </Paper>

             {loading ? (
  <Box textAlign="center" py={10}>
    <CircularProgress size={30} thickness={5} sx={{ color: themeColor }} />
  </Box>
) : data.length === 0 ? (
  <Paper
    elevation={0}
    sx={{
      p: 6,
      textAlign: "center",
      borderRadius: "16px",
      border: "1px dashed #cbd5e1",
      bgcolor: "#f8fafc",
    }}
  >
    <Box
      sx={{
        width: 60,
        height: 60,
        mx: "auto",
        mb: 2,
        borderRadius: "14px",
        bgcolor: "#eef2ff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#6366f1",
      }}
    >
      <FiDatabase size={26} />
    </Box>

    <Typography variant="h6" fontWeight={800} color="#334155">
      No Records Found
    </Typography>

    <Typography
      variant="body2"
      sx={{ color: "#64748b", mt: 0.5, maxWidth: 420, mx: "auto" }}
    >
      There are no schedules or execution logs available for the selected
      date range.
    </Typography>
  </Paper>
) : (
  data.map((item) => {
    // History rows key on `_id`; simulated runs key on `id` (`run_<sch>_<ts>`).
    const rowId = String(item._id || item.id);
    // A simulated run's own time is the TOP-LEVEL nextRunAt. `when.nextRunAt`
    // is the schedule's single next fire time, identical on every occurrence of
    // that schedule, so reading it here printed one timestamp on every row.
    const runAt = resolveRunAt(item);
    return (
                  <Paper
                    key={rowId}
                    elevation={0}
                    sx={{
                      mb: 2,
                      borderRadius: "16px",
                      border: "1px solid #e2e8f0",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      onClick={() =>
                        setExpandedId(expandedId === rowId ? null : rowId)
                      }
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor:
                          expandedId === rowId ? `${themeColor}05` : "white",
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: themeColor,
                            color: "white",
                            borderRadius: "10px",
                            width: 36,
                            height: 36,
                          }}
                        >
                          {mode === "history" ? (
                            <FiDatabase size={16} />
                          ) : (
                            <FiZap size={16} />
                          )}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={800}>
                            {item.name || item.scheduleName}
                          </Typography>
                            <DetailRow
                                label="Strategy"
                                value={item.when?.strategy}
                              />
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <FiClock size={12} />{" "}
                            {formatDisplay(
                              runAt,
                              item?.when?.calenderType === "hebrew",
                            )}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        flexShrink={0}
                      >
                        <Chip
                          label={item.cronstatus || item.status}
                          size="small"
                          sx={{
                            fontWeight: 900,
                            bgcolor: themeColor,
                            color: "white",
                            fontSize: "0.6rem",
                          }}
                        />
                        {/* The row shows only the run. The full rule -
                            recurrence, zman, timezone, pause, per-device
                            outcome - lives in the dialog. */}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<FiEye size={14} />}
                          title="View full schedule details"
                          onClick={(e) => {
                            // Do not also collapse/expand the row underneath.
                            e.stopPropagation();
                            setDetailItem(item);
                          }}
                          sx={{
                            fontWeight: 800,
                            fontSize: "0.65rem",
                            borderRadius: "10px",
                            textTransform: "none",
                            borderColor: "#cbd5e1",
                            color: "#475569",
                            "&:hover": {
                              borderColor: themeColor,
                              color: themeColor,
                              bgcolor: `${themeColor}08`,
                            },
                          }}
                        >
                          View
                        </Button>
                      </Stack>
                    </Box>
                    <Collapse in={expandedId === rowId}>
                      <Box
                        sx={{
                          p: 3,
                          bgcolor: "#f8fafc",
                          borderTop: "1px solid #e2e8f0",
                        }}
                      >
                        <Grid container spacing={2}>
                        
                          <Grid item xs={12} md={7}>
                            <Typography
                              variant="overline"
                              sx={{ color: "#64748b", fontWeight: 900 }}
                            >
                              Payload
                            </Typography>
 <Box
  sx={{
    mt: 1,
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
  }}
>
  {item.actions?.map((act, i) => (
    <Box
      key={i}
      sx={{
        width: item.actions?.length === 1 ? "100%" : "calc(50% - 8px)", // exact 2 columns
        p: 2,
        bgcolor: "white",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        minHeight: 110,
        transition: "all .2s",
        "&:hover": {
          borderColor: "#6366f1",
          boxShadow: "0 6px 16px rgba(0,0,0,0.05)",
        },
      }}
    >
      <Typography variant="body2" fontWeight={800}>
        {act.deviceAlias || "Unknown Device"}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: "#94a3b8",
          fontFamily: "monospace",
          wordBreak: "break-all",
        }}
      >
        {act.deviceId}
      </Typography>

      <Stack direction="row" spacing={1} mt={1}>
        <Chip
          label={act.deviceOn ? "POWER ON" : "POWER OFF"}
          size="small"
          color={act.deviceOn ? "success" : "default"}
        />

        {act.brightnessSupported && (
          <Chip
            label={`Brightness ${act.brightnessLevel}%`}
            size="small"
          />
        )}
      </Stack>

      {act.remark && (
        <Typography
          variant="caption"
          sx={{ mt: 1, display: "block", color: "#ef4444" }}
        >
          {act.remark}
        </Typography>
      )}
    </Box>
  ))}
</Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </Paper>
    );
  })
              )}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  size="small"
                  color="primary"
                />
              </Box>
            </>
          ) : (
            /* --- HARDWARE REGISTRY (GAP FIXED) --- */
            /* --- 1. THE REGISTRY CONTAINER --- */
            <Box sx={{ width: "100%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ color: "#1e293b" }}
                >
                  Devices : {filteredDevices.length} / {allDevices.length}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{ p: 0.5, bgcolor: "#f1f5f9", borderRadius: "10px" }}
                >
                  <ToggleButtonGroup
                    value={deviceFilter}
                    exclusive
                    onChange={(e, v) => v && setDeviceFilter(v)}
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root": {
                        border: "none",
                        fontWeight: 800,
                        px: 2,
                      },
                    }}
                  >
                    <ToggleButton value="all">ALL</ToggleButton>
                    <ToggleButton value="online">ONLINE</ToggleButton>
                    <ToggleButton value="offline">OFFLINE</ToggleButton>
                    <ToggleButton value="restore">SMART RESTORE</ToggleButton>
                  </ToggleButtonGroup>
                </Paper>
              </Stack>

              {/* Using a Stack instead of a Grid removes the "staggered" height issue */}
              <Stack spacing={1.5} sx={{ width: "100%" }}>
                {filteredDevices.map((device) => (
                  <EnhancedDeviceCard
                    key={device._id}
                    device={device}
                    onFormat={formatDisplay}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>

      <OverviewScheduleDialog
        open={Boolean(detailItem)}
        onClose={() => setDetailItem(null)}
        item={detailItem}
        mode={mode}
        themeColor={themeColor}
        formatDisplay={formatDisplay}
      />
    </Box>
  );
}

// --- SUB-COMPONENTS ---

function EnhancedDeviceCard({ device, onFormat }) {
  const [open, setOpen] = useState(false);
  const isOnline = device.status === "online";
  const hasSR = device.smartRestore?.enabled;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        width: "100%",
        overflow: "hidden",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "#6366f1",
          bgcolor: "#fcfdff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
        },
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          p: 2,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* LEFT: DEVICE IDENTITY */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: isOnline ? "#f0fdf4" : "#fef2f2",
              color: isOnline ? "#10b981" : "#ef4444",
              border: `1px solid ${isOnline ? "#dcfce7" : "#fee2e2"}`,
            }}
          >
            {isOnline ? <FiWifi size={22} /> : <FiWifiOff size={22} />}
          </Avatar>
          <Box>
            <Typography
              variant="body2"
              fontWeight={800}
              sx={{ color: "#1e293b", fontSize: "0.9rem" }}
            >
              {/* Same rule as every other screen: the user's own name first,
                  then the provider's. */}
              {deviceNameOnly(device) || "Unnamed Device"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#94a3b8",
                fontFamily: "monospace",
                letterSpacing: 0.5,
              }}
            >
              {device.meta?.mac?.toUpperCase() || "NO-MAC-ADDR"}
            </Typography>
          </Box>
        </Stack>

        {/* RIGHT: STATUS & POWER */}
        <Stack direction="row" spacing={4} alignItems="center">
          {hasSR && (
            <Chip
              icon={<FiRotateCcw size={12} />}
              label={
                device.smartRestore?.expectedState === device.deviceOn
                  ? "SR SYNCED"
                  : "SR PENDING"
              }
              size="small"
              sx={{
                bgcolor:
                  device.smartRestore?.expectedState === device.deviceOn
                    ? "#ecfdf5"
                    : "#fef3c7",
                color:
                  device.smartRestore?.expectedState === device.deviceOn
                    ? "#059669"
                    : "#b45309",
                fontWeight: 900,
                fontSize: "0.65rem",
                height: 22,
              }}
            />
          )}

          <Box sx={{ textAlign: "right", minWidth: 90 }}>
            <Typography
              variant="caption"
              fontWeight={900}
              sx={{
                color: device.deviceOn ? "#10b981" : "#64748b",
                display: "block",
                fontSize: "0.7rem",
              }}
            >
              {device.deviceOn ? "● POWER ON" : "○ POWER OFF"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isOnline ? "#10b981" : "#ef4444",
                fontSize: "0.65rem",
                fontWeight: 700,
              }}
            >
              {isOnline ? "ONLINE" : "OFFLINE"}
            </Typography>
          </Box>

          <Box
            sx={{
              color: "#cbd5e1",
              transform: open ? "rotate(180deg)" : "none",
              transition: "0.2s",
              display: "flex",
            }}
          >
            <FiTrendingUp style={{ transform: "rotate(90deg)" }} />
          </Box>
        </Stack>
      </Box>

      {/* EXPANDED SECTION: TECHNICAL SPECS */}
      <Collapse in={open}>
        <Divider sx={{ borderStyle: "dashed", borderColor: "#e2e8f0" }} />
        <Box sx={{ p: 2.5, bgcolor: "#f8fafc" }}>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <DetailRow
                label="Model Type "
                value={device.meta?.model || "Standard"}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <DetailRow
                label="Firmware "
                value={device.meta?.fwVer || "v1.0.0"}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <DetailRow
                label="Network IP "
                value={device.meta?.ip || "192.168.1.XX"}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <DetailRow
                label="Signal Strength "
                value={
                  device.meta?.rssi ? `${device.meta.rssi} dBm` : "Excellent"
                }
              />
            </Grid>
          </Grid>
          {hasSR && (
            <>
              <Divider sx={{ my: 2, borderStyle: "dashed" }} />

              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "#c2410c", fontWeight: 900 }}
                >
                  Smart Restore Intelligence
                </Typography>

                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: "#fff7ed",
                    border: "1px solid #fed7aa",
                  }}
                >
                  <DetailRow
                    label="Expected State"
                    value={
                      device.smartRestore?.expectedState === null
                        ? "Not Set"
                        : device.smartRestore.expectedState
                          ? "ON"
                          : "OFF"
                    }
                    highlight
                  />

                  {device.smartRestore?.expectedBrightness !== null && (
                    <DetailRow
                      label="Expected Brightness"
                      value={`${device.smartRestore.expectedBrightness}%`}
                    />
                  )}

                  <DetailRow
                    label="Source Schedule"
                    value={
                      device.smartRestore?.sourceSchedule
                        ? `Schedule name: ${device.smartRestore.sourceSchedule.name} • Status: ${device.smartRestore.sourceSchedule.status}`
                        : "Manual"
                    }
                  />

                  {/* <DetailRow
          label="Last Updated"
          value={
            device.smartRestore?.updatedAt
              ? onFormat(device.smartRestore.updatedAt)
              : "—"
          }
        /> */}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

function HeaderMetric({ label, value, color = "white" }) {
  return (
    <Box textAlign="center">
      <Typography variant="h5" fontWeight={900} sx={{ color, lineHeight: 1 }}>
        {value || 0}
      </Typography>
      <Typography
        variant="caption"
        sx={{ opacity: 0.5, fontWeight: 800, fontSize: "0.6rem" }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function NavButton({ value, icon, label }) {
  return (
    <ToggleButton
      value={value}
      sx={{
        px: 3,
        py: 1,
        border: "none !important",
        borderRadius: "10px !important",
        fontWeight: 800,
        fontSize: "0.7rem",
      }}
    >
      {React.cloneElement(icon, {
        style: { marginRight: 6, fontSize: "0.9rem" },
      })}{" "}
      {label}
    </ToggleButton>
  );
}

function DashboardSection({ title, icon, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        width: "100%",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Box
          sx={{
            p: 0.7,
            bgcolor: "#f1f5f9",
            borderRadius: "6px",
            color: "#6366f1",
            display: "flex",
          }}
        >
          {icon}
        </Box>
        <Typography variant="caption" fontWeight={900}>
          {title.toUpperCase()}
        </Typography>
      </Stack>
      {children}
    </Paper>
  );
}

function DetailRow({ label, value, highlight = false }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="textSecondary"
        sx={{ fontSize: "0.7rem" }}
      >
        {label} :{" "}
      </Typography>
      <Typography
        variant="caption"
        fontWeight={900}
        sx={{ color: highlight ? "#6366f1" : "inherit", fontSize: "0.7rem" }}
      >
        {" "}
        {value || "---"}
      </Typography>
    </Box>
  );
}


